import { useState } from "react";
import { Copy, MessageCircle, UserPlus, Users } from "lucide-react";
import {
  useCanAccess,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { Section } from "./ClientShow";

type PortalUserRow = {
  id: Identifier;
  first_name: string | null;
  last_name: string | null;
  email: string;
  disabled: boolean;
};

type InviteResult = { link: string; name: string };

const KNOWN_ERRORS = ["email_is_staff", "email_other_client"];

export const PortalAccessCard = ({
  companyId,
  whatsapp,
}: {
  companyId: Identifier;
  whatsapp?: string | null;
}) => {
  const translate = useTranslate();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "client_contracts",
    action: "edit",
  });
  const [isInviting, setIsInviting] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);
  const { data: people = [] } = useGetList<PortalUserRow>("portal_users", {
    filter: { company_id: companyId },
    sort: { field: "created_at", order: "ASC" },
    pagination: { page: 1, perPage: 50 },
  });

  return (
    <Section
      icon={<Users className="size-4" />}
      title={translate("crm.portal.invite.people")}
    >
      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.portal.invite.no_people")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {people.map((person) => (
            <li key={person.id} className="text-sm">
              <span className="font-medium">
                {[person.first_name, person.last_name]
                  .filter(Boolean)
                  .join(" ") || person.email}
              </span>
              <span className="block text-xs text-muted-foreground">
                {person.email}
              </span>
            </li>
          ))}
        </ul>
      )}

      {isAdmin ? (
        <div className="mt-4 border-t border-border pt-4">
          {result ? (
            <InviteLink
              result={result}
              whatsapp={whatsapp}
              onNew={() => {
                setResult(null);
                setIsInviting(true);
              }}
            />
          ) : isInviting ? (
            <InviteForm
              companyId={companyId}
              onCancel={() => setIsInviting(false)}
              onCreated={(created) => {
                setIsInviting(false);
                setResult(created);
              }}
            />
          ) : (
            <Button
              className="w-full rounded-full"
              onClick={() => setIsInviting(true)}
            >
              <UserPlus className="size-4" />
              {translate("crm.portal.invite.title")}
            </Button>
          )}
        </div>
      ) : null}
    </Section>
  );
};

const InviteForm = ({
  companyId,
  onCancel,
  onCreated,
}: {
  companyId: Identifier;
  onCancel: () => void;
  onCreated: (result: InviteResult) => void;
}) => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(translate("ra.validation.email"));
      return;
    }
    setIsSaving(true);
    const { data, error: invokeError } =
      await getSupabaseClient().functions.invoke("invite_client", {
        body: {
          company_id: companyId,
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          redirect_to: `${window.location.origin}${window.location.pathname.replace(/[^/]*$/, "")}auth-callback.html`,
        },
      });
    setIsSaving(false);
    if (invokeError || !data?.link) {
      let code: string | undefined;
      try {
        code = (await invokeError?.context?.json())?.code;
      } catch {
        code = undefined;
      }
      setError(
        translate(
          code && KNOWN_ERRORS.includes(code)
            ? `crm.portal.invite.errors.${code}`
            : "crm.portal.invite.errors.generic",
        ),
      );
      return;
    }
    refresh();
    onCreated({ link: data.link, name: firstName.trim() || email.trim() });
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <p className="text-xs text-muted-foreground">
        {translate("crm.portal.invite.description")}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-first">
            {translate("crm.portal.invite.first_name")}
          </Label>
          <Input
            id="invite-first"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-last">
            {translate("crm.portal.invite.last_name")}
          </Label>
          <Input
            id="invite-last"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="invite-email">
          {translate("crm.portal.invite.email")}
        </Label>
        <Input
          id="invite-email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
        />
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" className="rounded-full" disabled={isSaving}>
          {translate("crm.portal.invite.create_link")}
        </Button>
      </div>
    </form>
  );
};

const InviteLink = ({
  result,
  whatsapp,
  onNew,
}: {
  result: InviteResult;
  whatsapp?: string | null;
  onNew: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const message = translate("crm.portal.invite.whatsapp_message", {
    name: result.name,
    link: result.link,
  });
  const whatsappDigits = whatsapp?.replace(/\D/g, "") ?? "";
  const whatsappUrl = `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(message)}`;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-primary">
        {translate("crm.portal.invite.link_ready")}
      </p>
      <p className="text-xs text-muted-foreground">
        {translate("crm.portal.invite.link_hint")}
      </p>
      <Button
        variant="outline"
        className="rounded-full"
        onClick={async () => {
          await navigator.clipboard.writeText(result.link);
          notify("crm.portal.invite.copied", { type: "success" });
        }}
      >
        <Copy className="size-4" />
        {translate("crm.portal.invite.copy")}
      </Button>
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-center gap-2 rounded-full bg-[#25d366] px-4 py-2 text-sm font-semibold text-[#06301a] no-underline hover:opacity-90"
      >
        <MessageCircle className="size-4" />
        {translate("crm.portal.invite.send_whatsapp")}
      </a>
      <button
        type="button"
        onClick={onNew}
        className="text-xs text-muted-foreground underline"
      >
        {translate("crm.portal.invite.new_link")}
      </button>
    </div>
  );
};
